import { Link } from "react-router-dom";
import { Leaf, GraduationCap, Pencil, Camera } from "lucide-react";
import styles from "./AboutPage.module.css";

const credentials = [
  { icon: Leaf, line1: "Founder", line2: "since 2024" },
  { icon: GraduationCap, line1: "Applied Arts", line2: "Graduate" },
  { icon: Pencil, line1: "Original", line2: "Illustrator" },
  { icon: Camera, line1: "Traveler &", line2: "Story Collector" },
];

export default function AboutPage() {
  return (
    <div className={styles.aboutPage}>
      {/* ── Our Story ── */}
      <section className={styles.storySection}>
        <div className={styles.storySplit}>
          <div className={styles.storyText}>
            <h2 className={styles.splitHeading}>
              Our <em className={styles.script}>Story</em>
            </h2>
            <p className={styles.p}>
              Founded in <strong>February 2024</strong>, ArtsyIcile was born
              from a simple belief: art isn't meant to exist only in
              galleries—it should be part of our everyday lives.
            </p>
            <p className={styles.p}>
              The brand also began as a dream. A dream to create something that
              could bring art beyond museums and studios, into people's homes,
              routines, travels, and memories. A world where anyone—whether
              they've painted before or are picking up a brush for the very
              first time—can experience the joy, peace, and inspiration that
              creativity brings.
            </p>
            <p className={styles.p}>
              What makes ArtsyIcile different is that every collection begins
              long before it becomes a paint-by-number kit.
            </p>
            <p className={styles.p}>
              As one of Egypt's pioneering brands dedicated to{" "}
              <strong>original</strong> paint-by-number collections, we don't
              work from ready-made artwork or stock illustrations. Every design
              starts as an original concept created by our founder and artist.
              It may begin with a feeling, a meaningful memory, a place
              explored, the beauty of nature, or a photograph captured during a
              journey. From there, each artwork is sketched, illustrated, and
              carefully transformed into a paint-by-number experience while
              preserving the story behind it.
            </p>
            <p className={styles.p}>
              Every collection is designed entirely in-house—from the first
              inspiration and sketch to the final artwork and paint-by-number
              layout—making every ArtsyIcile piece exclusive to our community.
            </p>
            <p className={styles.p}>
              Our mission goes far beyond creating products. We're building a
              creative community that believes art should be accessible,
              personal, and woven into everyday life. Through original
              collections and meaningful experiences, we hope to inspire people
              to slow down, reconnect with themselves, reduce stress,
              strengthen relationships, and discover creativity in the moments
              that surround them every day.
            </p>
            <p className={styles.p}>
              This is only the beginning of a much bigger vision: to grow
              ArtsyIcile into a globally recognized creative lifestyle brand
              that inspires millions through original products, immersive
              experiences, and spaces where art, community, and everyday life
              come together.
            </p>
            <p className={`${styles.p} ${styles.missionTagline}`}>
              Where Life Becomes Art.
            </p>
          </div>

          <div className={styles.storyImages}>
            <img
              src="/About page/Artboard 1.png"
              alt="Landscape inspiration"
              className={styles.artboardImg}
              loading="lazy"
            />
            <img
              src="/About page/Artboard 2.png"
              alt="Creative journey"
              className={styles.artboardImg}
              loading="lazy"
            />
            <img
              src="/About page/Artboard 3.png"
              alt="Original sketch"
              className={styles.artboardImg}
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Left brush — Our Story ↔ Meet the Founder border */}
      <div className={styles.brushAnchorLeft} aria-hidden="true">
        <img src="/brush_1.png" className={styles.brushLeft} alt="" />
      </div>

      {/* ── Meet the Founder ── */}
      <section className={styles.founderSection}>
        <div className={styles.founderSplit}>
          {/* 1 — heading block (comes first in DOM so it's first on mobile) */}
          <div className={styles.founderIntro}>
            <h2 className={styles.splitHeading}>
              Meet the <em className={styles.script}>Founder</em>
            </h2>
            <h3 className={styles.founderName}>Icile Dawa</h3>
            <p className={styles.founderRole}>Founder • Artist • Designer</p>
            <hr className={styles.divider} />
          </div>

          {/* 2 — photo (second in DOM so it's second on mobile) */}
          <div className={styles.founderPhotoWrap}>
            <img
              src="/founder-icile-dawa.jpg"
              alt="Icile Dawa, founder of ArtsyIcile"
              className={styles.founderPhoto}
            />
          </div>

          {/* 3 — body text (third in DOM so it's last on mobile) */}
          <div className={styles.founderBody}>
            <p className={styles.p}>
              Hi, I'm Icile Dawa, the artist, designer, and founder of
              ArtsyIcile.
            </p>
            <p className={styles.p}>
              For as long as I can remember, I knew that art would always be
              part of my life. As a young artist, I dreamed of creating
              something of my own—something that could carry my ideas, feelings,
              and imagination into the world and help others experience art in a
              more personal way.
            </p>
            <p className={styles.p}>
              I have always seen art as more than something beautiful to look
              at. To me, it is a way of feeling, expressing, remembering, and
              seeing life differently. I wanted to share that perspective with
              people and show them that creativity is not limited to artists. It
              can belong to anyone.
            </p>
            <p className={styles.p}>That dream became ArtsyIcile.</p>
            <p className={styles.p}>
              Through this brand, I want to create a world where people feel
              closer to art, even if they have never painted before. A world
              that encourages them to slow down, connect with themselves,
              express their creativity, and discover the beauty hidden in
              everyday life.
            </p>
            <p className={styles.p}>
              Everything I create carries a part of my artistic vision, but the
              purpose goes beyond my own work. My message is to help people
              believe that they can create, feel, and experience art in their
              own way.
            </p>
            <p className={styles.p}>
              ArtsyIcile is the beginning of the world I once imagined as a
              young artist—and the way I hope to leave something meaningful
              through art.
            </p>

            <div className={styles.credentials}>
              {credentials.map(({ icon: Icon, line1, line2 }, i) => (
                <div key={i} className={styles.credItem}>
                  <Icon size={22} className={styles.credIcon} />
                  <span className={styles.credLine}>{line1}</span>
                  <span className={styles.credLine}>{line2}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Right brush — Meet the Founder ↔ Our Mission border */}
      <div className={styles.brushAnchorRight} aria-hidden="true">
        <img src="/brush_2.png" className={styles.brushRight} alt="" />
      </div>

      {/* ── Our Mission ── */}
      <section className={styles.missionSection}>
        <div className={styles.missionSplit}>
          {/* 1 — heading (first on mobile) */}
          <div className={styles.missionIntro}>
            <h2 className={styles.splitHeading}>
              Our <em className={styles.script}>Mission</em>
            </h2>
            <hr className={styles.divider} />
          </div>

          {/* 2 — image (second on mobile) */}
          <div className={styles.missionImageWrap}>
            <img
              src="/our mission.jpeg"
              alt="Our mission"
              className={styles.missionImage}
            />
          </div>

          {/* 3 — body text (last on mobile) */}
          <div className={styles.missionBody}>
            <p className={styles.p}>
              To make art part of everyday life by creating original artistic
              experiences inspired by real places, meaningful stories, and
              original illustrations—helping people slow down, express
              themselves, and discover the therapeutic joy of creating.
            </p>
            <p className={`${styles.p} ${styles.missionTagline}`}>
              Where Life Becomes Art.
            </p>
            <Link to="/products/kits" className={styles.ctaButton}>
              Explore Collections →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
